import { createAsyncThunk } from '@reduxjs/toolkit'
import { instance } from '../api/api'
import { GetMenuResponseType, MenuType } from './types'

export const getAllMenu = createAsyncThunk(
  'menu/getAllMenu',
  async (
    {
      token,
      page,
      name,
      search,
      description,
      itemsPerPage,
      categoryIds,
    }: {
      token: string
      page: number
      name?: string
      search?: string
      itemsPerPage: number
      description?: string
      categoryIds?: string[]
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<GetMenuResponseType>('menu', {
        params: {
          ...(page ? { page: page } : {}),
          ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
          ...(search ? { search: search } : {}),
          ...(name ? { name: name } : {}),
          ...(description ? { description: description } : {}),
          ...(categoryIds ? { categoryIds: categoryIds } : {}),
        },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Handle 401 error (e.g., redirect to login, notify user)
        return thunkAPI.rejectWithValue('Unauthorized')
      }

      // Handle other errors
      return thunkAPI.rejectWithValue(error?.response?.data?.message || 'An error occurred.')
    }
  },
)

export const getMenuFromCompany = createAsyncThunk(
  'menu/getMenuFromCompany',
  async (
    {
      token,
      page,
      itemsPerPage,
      search,
    }: {
      token: string
      page: number
      itemsPerPage: number
      search?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<GetMenuResponseType>('menu', {
        params: {
          ...(page ? { page: page } : {}),
          ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
          ...(search ? { search: search } : {}),
        },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getCurrentMenu = createAsyncThunk(
  'menu/getCurrentMenu',
  async ({ menuId, token }: { menuId: string | undefined; token: string }, thunkAPI) => {
    try {
      const response = await instance.get<MenuType>(`menu/${menuId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const createMenu = createAsyncThunk(
  'menu/createMenu',
  async ({ data, token }: { data: any; token: string }, thunkAPI) => {
    try {
      const response = await instance.post<any>('menu', data, {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Something went wrong')
    }
  },
)

export const deleteCurrentMenu = createAsyncThunk(
  'menu/deleteCurrentMenu',
  async ({ menuId, token }: { menuId: string | undefined; token: string }, thunkAPI) => {
    try {
      const response = await instance.delete<MenuType>(`menu/${menuId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const deleteCurrentFileFromMenu = createAsyncThunk(
  'menu/deleteCurrentFileFromMenu',
  async (
    {
      menuId,
      filesToDelete,
      token,
    }: { menuId: string | undefined; filesToDelete: string[]; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.delete<MenuType>(`menu/${menuId}/files`, {
        data: { filesToDelete },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const changeMenuMainInformation = createAsyncThunk(
  'menu/changeMenuMainInformation',
  async (
    {
      token,
      menuId,
      name,
      description,
      categoryIds = [],
    }: {
      menuId: string | number
      name: string
      description: string
      token: string
      categoryIds?: string[]
    },
    thunkAPI,
  ) => {
    try {
      const removeAllCategories = categoryIds.length === 0
      const response = await instance.patch<MenuType>(
        `menu/${menuId}`,
        {
          description,
          name,
          categoryIds: removeAllCategories ? [] : categoryIds,
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )
      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

async function fetchExistingMenuCategories(menuId: string | number): Promise<any[]> {
  const response = await instance.get(`/menu/${menuId}`)
  return response.data.menuCategories || []
}

export const changeMenuDescription = createAsyncThunk(
  'menu/changeMenuDescription',
  async (
    {
      menuId,
      description,
      token,
    }: { menuId: string | number | undefined; description: string; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<MenuType>(
        `menu/${menuId}`,
        {
          description,
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)
